import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import Logo from "../components/Logo";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Logo showText linkTo="/" />
          
          <Link to="/">
            <Button variant="ghost" className="text-text-muted hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      <main className="pt-24 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-8">Privacy Policy</h1>
          
          <div className="prose prose-invert max-w-none space-y-8 text-text-muted">
            <p className="text-lg">
              Last updated: January 15, 2026
            </p>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">1. Information We Collect</h2>
              <p className="mb-4">When you use ReplyDriven, we collect:</p>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li><strong className="text-white">Account Information:</strong> Your name, email address, and profile picture from Google OAuth.</li>
                <li><strong className="text-white">Shopify Store Data:</strong> Read-only access to orders and products data for analysis purposes.</li>
                <li><strong className="text-white">Usage Data:</strong> How you interact with our service to improve the experience.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">2. How We Use Your Information</h2>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>To provide weekly profit leak analysis for your Shopify store</li>
                <li>To send you email reports (if enabled)</li>
                <li>To improve our AI analysis algorithms</li>
                <li>To communicate important service updates</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">3. Data Security</h2>
              <p>
                We implement industry-standard security measures to protect your data. Shopify access tokens are stored securely. We only request read-only access to your store - we cannot modify any of your Shopify data.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">4. Data Sharing</h2>
              <p className="mb-3">
                We do not sell, trade, or rent your personal information to third parties. Your Shopify data is used solely for generating insights for your store.
              </p>
              <p className="text-text-muted text-sm">
                We use trusted third-party services (such as email providers and payment processors) solely to deliver reports, process payments, and provide essential service communications. These providers are contractually bound to protect your data and may not use it for any other purpose.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">5. Your Rights</h2>
              <ul className="list-disc list-inside space-y-2 pl-4">
                <li>Access your data at any time through the dashboard</li>
                <li>Disconnect your Shopify store at any time</li>
                <li>Delete your account and all associated data</li>
                <li>Opt out of email reports</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">6. Cookies & Analytics</h2>
              <p>
                We use essential cookies to maintain your session and keep you logged in. We may also use analytics tools (such as PostHog and Google Analytics) to understand how our product is used and improve the experience. We do not use advertising or tracking cookies.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">7. Payment Processing</h2>
              <p>
                Subscriptions are managed through Shopify's billing system. When you subscribe to ReplyDriven through the Shopify App Store, all billing, payment processing, and invoicing is handled directly by Shopify. We do not store your credit card information. You can manage your subscription, view invoices, and cancel at any time through your Shopify Admin.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">8. Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. We will notify you of any significant changes via email.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-heading font-semibold text-white mb-4">9. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at{" "}
                <a href="mailto:hello@replydriven.com" className="text-[#D4F34A] hover:underline">
                  hello@replydriven.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 md:px-12 border-t border-[#262626]">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <Logo size="sm" showText linkTo="/" />
          
          <div className="flex items-center gap-6 text-sm text-text-muted">
            <a href="mailto:hello@replydriven.com" className="hover:text-white transition-colors">Contact</a>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          </div>
          
          <p className="text-sm text-text-muted">
            © {new Date().getFullYear()} ReplyDriven. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
